import { useCallback, useMemo } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import {
  connectionRows,
  GetFavoriteIssuesDocument,
  type IssueRow,
  RemoveFavoriteDocument,
} from "#app/lib/data";
import { toastManager } from "@salestrak/ui";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { toastMutationError } from "#app/lib/utils/toast-errors";

export function useFavoriteIssues(opts: { skip?: boolean } = {}) {
  const { favoriteIds } = useWorkspaceData();

  const favoriteIssueIds = useMemo(() => [...favoriteIds], [favoriteIds]);

  const variables = useMemo(
    () => ({ favoriteIssueIds }),
    [favoriteIssueIds],
  );

  const { data, loading } = useQuery(GetFavoriteIssuesDocument, {
    variables,
    skip: favoriteIssueIds.length === 0 || (opts.skip ?? false),
    fetchPolicy: "cache-first",
  });

  const issues: IssueRow[] = useMemo(
    () => (favoriteIssueIds.length === 0 ? [] : connectionRows(data?.favoriteIssues) ?? []),
    [data, favoriteIssueIds],
  );

  const [removeMutation] = useMutation(RemoveFavoriteDocument);

  const removeFavorite = useCallback(
    async (issueId: string) => {
      try {
        await removeMutation({
          variables: { type: "issue", referenceId: issueId },
        });
        toastManager.add({ title: "Removed from favorites", type: "success" });
      } catch (err) {
        toastMutationError(err, "Failed to remove from favorites");
      }
    },
    [removeMutation],
  );

  return {
    issues,
    favoriteIds,
    isLoading: loading && issues.length === 0,
    removeFavorite,
  };
}
